import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@mae/icons/base-icons';
import { isIconDefinition } from '@mae/icons/utils';
import { MAEDefaultI18NextProvider } from '@mae/localization';
import { useLocalStorage } from '@mae/utils/hooks/use-local-storage';
import { Tooltip } from '@mui/material';
import List from '@mui/material/List';
import Popper from '@mui/material/Popper';
import React from 'react';
import { FlexScrollBarWrapper, ScrollBar } from '../scroll-bar';
import { MAEDefaultThemeProvider } from '../themes';
import { Collapse } from './collapse';
import { CollapseToggles } from './collapse-toggles';
import { ContextMenu } from './context-menu';
import { Drawer } from './drawer';
import { ExpandIcon } from './expand-icon';
import { ListItem } from './list-item';
import { ListItemIcon } from './list-item-icon';
import { ListItemText } from './list-item-text';
import { ListSubItems } from './list-sub-items';
import { Root } from './root';
function getActive(route, menuItems) {
    if (!route) {
        return {
            parentKey: '',
            subItemKey: menuItems[0].subItems.length ? menuItems[0].subItems[0].key : menuItems[0].key
        };
    }
    // slicing to 2 items since we only support 2 deep menu items
    const parts = route.split('/').slice(0, 2);
    const result = {};
    if (parts.length === 2) {
        result.parentKey = parts[0];
        result.subItemKey = parts.join('/');
    }
    if (parts.length === 1) {
        // get top level
        const item = menuItems.find((item) => item.key === parts[0]);
        if (item.subItems.length) {
            // default view
            result.parentKey = item.key;
            result.subItemKey = item.subItems[0].key;
        }
        else {
            result.parentKey = route;
            result.subItemKey = route;
        }
    }
    return result;
}
const storageKey = 'mae-component-menu';
export function Menu(props) {
    const selected = getActive(props.defaultSelection, props.menuItems);
    const isRtl = document.body.classList.contains('rtl');
    const [storage, storageUpdate] = useLocalStorage(storageKey, {
        open: true,
        pinned: props.collapsable === false || true
    });
    // state
    const [contextMenuData, setContextMenuData] = React.useState(null);
    const [nestedOpen, setNestedOpen] = React.useState(selected.parentKey);
    const [selectedView, setSelectedView] = React.useState(selected.subItemKey);
    function setActive(route) {
        const full = route.slice(0, 2).join('/');
        const active = getActive(full, props.menuItems);
        if (!active) {
            return;
        }
        setNestedOpen(active.parentKey);
        setSelectedView(active.subItemKey);
    }
    function toggleDrawer() {
        handleContextMenuClose();
        storageUpdate('open', !storage.open);
    }
    function handlePin() {
        storageUpdate('pinned', !storage.pinned);
    }
    function handleCustomTopLevel(item) {
        if (item.disabled) {
            return;
        }
        if (item.subItems.length === 0) {
            if (item.selectable) {
                if (!storage.pinned) {
                    storageUpdate('open', false);
                }
                setSelectedView(item.key);
            }
            if (!item.disabled) {
                item.action();
            }
        }
        else {
            const newItem = item.key === nestedOpen ? 0 : item.key;
            handleContextMenuClose();
            setNestedOpen(newItem);
        }
    }
    function handleCustomSubItemSelection(subItem) {
        if (subItem.selectable) {
            if (!storage.pinned) {
                storageUpdate('open', false);
            }
            setSelectedView(subItem.key);
        }
        if (!subItem.disabled) {
            handleContextMenuClose();
            subItem.action();
        }
    }
    function handleContextMenuClose() {
        setContextMenuData(null);
    }
    // Handle updates from backbone view change events
    props.menuItems.forEach((menuItem) => (menuItem.onActivate = (route) => {
        if (!route) {
            return;
        }
        setActive(route);
    }));
    const topCustomLevel = props.menuItems.map((item) => {
        const shouldShowContext = nestedOpen !== item.key && !item.disabled && item.subItems.length !== 0;
        const shouldHideTooltip = storage.open || (shouldShowContext && item.subItems.length !== 0);
        return (_jsxs(React.Fragment, { children: [_jsxs(ListItem, { disabled: item.disabled, onMouseEnter: (event) => {
                        // don't show the menu if the main menu is already expanded
                        if (shouldShowContext) {
                            setContextMenuData({
                                anchor: event.currentTarget,
                                title: item.label,
                                listItems: (_jsx(ListSubItems, { groupSelected: false, menuItems: item.subItems, selectedView: selectedView, onClick: (menuItem) => handleCustomSubItemSelection(menuItem), showTooltip: !storage.open }))
                            });
                        }
                        else {
                            handleContextMenuClose();
                        }
                    }, onClick: () => handleCustomTopLevel(item), selected: nestedOpen === item.key, children: [_jsx(Tooltip, { title: shouldHideTooltip ? '' : item.label, placement: "right", children: _jsxs(ListItemIcon, { selected: selectedView === item.key && item.subItems.length === 0, children: [item.icon && !item.maeIcon && (_jsx("div", { className: `${item.icon}`, children: item.icon ? undefined : item.label.substring(0, 1).toLocaleUpperCase() })), item.maeIcon &&
                                        (isIconDefinition(item.maeIcon) ? (_jsx(FontAwesomeIcon, { icon: item.maeIcon })) : (_jsx(FontAwesomeIcon, { ...item.maeIcon })))] }) }), _jsx(ListItemText, { selected: selectedView === item.key && item.subItems.length === 0, topLevelSelected: nestedOpen === item.key, primary: item.label }), item.subItems.length !== 0 && _jsx(ExpandIcon, { expanded: nestedOpen === item.key })] }), item.subItems.length !== 0 && (_jsx(Collapse, { in: nestedOpen === item.key || item.alwaysVisible, timeout: "auto", unmountOnExit: true, children: _jsx(List, { component: "div", disablePadding: true, children: _jsx(ListSubItems, { groupSelected: true, menuItems: item.subItems, selectedView: selectedView, onClick: (menuItem) => handleCustomSubItemSelection(menuItem), onMouseEnter: handleContextMenuClose, showTooltip: !storage.open }) }) }))] }, item.key));
    });
    const topPosition = props.collapseButtonOrientation === 'top';
    const collapsibleComponent = props.collapsable === undefined || props.collapsable ? (_jsx(CollapseToggles, { isRtl: isRtl, isTopPosition: topPosition, isDrawerOpen: storage.open, isPinned: storage.pinned, onPin: handlePin, onToggleDrawer: toggleDrawer })) : null;
    return (_jsx(MAEDefaultI18NextProvider, { children: _jsx(MAEDefaultThemeProvider, { children: _jsx(Root, { children: _jsxs(Drawer, { onMouseLeave: handleContextMenuClose, open: storage.open, variant: "permanent", children: [contextMenuData && (_jsx(Popper, { style: { zIndex: 9999 }, id: storageKey.concat('-popper-', contextMenuData.title), open: contextMenuData.anchor !== null, placement: "right-start", anchorEl: contextMenuData.anchor, children: _jsx(ContextMenu, { onClose: handleContextMenuClose, title: contextMenuData.title, listItems: contextMenuData.listItems }) })), topPosition && collapsibleComponent, _jsx(FlexScrollBarWrapper, { children: _jsx(ScrollBar, { "data-simplebar-direction": isRtl ? 'rtl' : 'ltr', children: _jsx(List, { component: "div", disablePadding: true, sx: {
                                        overflowX: 'clip'
                                    }, children: topCustomLevel }) }) }), !topPosition && collapsibleComponent] }) }) }) }));
}
export * from './menu-mae';
